import type {
  DashboardTableDateFilter,
  DashboardTableSortDirection,
  DashboardTableSortState,
} from '@fluenceur/types';

export interface DashboardTableQueryParamNames {
  page: string;
  perPage: string;
  sortBy: string;
  sortDirection: string;
  dateFilterType: string;
  startDate: string;
  endDate: string;
}

export interface DashboardTableFilterItem {
  key: string;
  label: string;
  value: string;
  onClear?: () => void;
}

export const getDashboardTableQueryParamNames = (
  namespace: string
): DashboardTableQueryParamNames => {
  const prefix = namespace.trim();

  if (!prefix) {
    throw new Error('Dashboard table query namespace is required');
  }

  return {
    page: `${prefix}Page`,
    perPage: `${prefix}PerPage`,
    sortBy: `${prefix}SortBy`,
    sortDirection: `${prefix}SortDirection`,
    dateFilterType: `${prefix}DateFilterType`,
    startDate: `${prefix}StartDate`,
    endDate: `${prefix}EndDate`,
  };
};

export const oppositeSortDirection = (
  direction: DashboardTableSortDirection
): DashboardTableSortDirection => (direction === 'asc' ? 'desc' : 'asc');

export const getNextDashboardTableSortState = <SortBy extends string>(
  current: DashboardTableSortState<SortBy>,
  column: SortBy,
  defaultSort: DashboardTableSortState<SortBy>,
  columnDefaultDirection: DashboardTableSortDirection = 'desc'
): DashboardTableSortState<SortBy> => {
  if (current.sortBy !== column) {
    return {
      sortBy: column,
      sortDirection: columnDefaultDirection,
    };
  }

  if (current.sortDirection === columnDefaultDirection) {
    return {
      sortBy: column,
      sortDirection: oppositeSortDirection(columnDefaultDirection),
    };
  }

  return defaultSort;
};

export const hasDashboardTableDateFilter = (dateFilter: DashboardTableDateFilter): boolean =>
  Boolean(dateFilter.dateFilterType || dateFilter.startDate || dateFilter.endDate);

export const formatDashboardTableDateRange = (
  dateFilter: Pick<DashboardTableDateFilter, 'startDate' | 'endDate'>,
  emptyValueLabel: string,
  rangeSeparator = ' - '
): string => {
  if (!dateFilter.startDate && !dateFilter.endDate) {
    return emptyValueLabel;
  }

  if (dateFilter.startDate && dateFilter.endDate) {
    return `${dateFilter.startDate}${rangeSeparator}${dateFilter.endDate}`;
  }

  return dateFilter.startDate ?? dateFilter.endDate ?? emptyValueLabel;
};

export const buildDashboardTableDateFilterItem = (
  dateFilter: DashboardTableDateFilter,
  labels: {
    label: string;
    dateFilterTypeLabel?: string;
    emptyValueLabel: string;
    rangeSeparator?: string;
  },
  onClear?: () => void
): DashboardTableFilterItem | null => {
  if (!hasDashboardTableDateFilter(dateFilter)) {
    return null;
  }

  const range = formatDashboardTableDateRange(
    dateFilter,
    labels.emptyValueLabel,
    labels.rangeSeparator
  );

  return {
    key: 'dateRange',
    label: labels.label,
    value: labels.dateFilterTypeLabel ? `${labels.dateFilterTypeLabel}: ${range}` : range,
    onClear,
  };
};
